import { IcPin } from '@assets/icons/24x24';
import { Center, Icon } from '@chakra-ui/react';
import { useWithDispatch } from '@hooks/useWithDispatch';
import { updatePopupByMapIdFromCockpit } from '@redux/maps/maps.reducer';
import { issueMapCommand } from '@redux/maps/mapsCommands.reducer';
import { useLazyGetDetailQuery } from '@services/data/notification.api';
import { SHOW_ON_MAP_BUTTON_ZOOM } from '@utils/map/mapZoom.constants';
import { useEffect, useState } from 'react';

import { NotificationType, NotificationTypeEnum } from '@/types/data/data.types';
import { MapIdEnum } from '@/types/map.types';
import { ExpiredDroneInfoData, InfoDataTypeEnum, PopupTypeEnum, TargetInfoData } from '@/types/popup.types';

type Props = {
  notificationLight: NotificationType;
  isExpired: boolean;
};

export function ShowOnMapButton({ notificationLight, isExpired }: Readonly<Props>) {
  const updatePopupFromCockpit = useWithDispatch(updatePopupByMapIdFromCockpit);
  const centerMapView = useWithDispatch(issueMapCommand);
  const [getDetail, { data, isLoading }] = useLazyGetDetailQuery();
  const [openExpired, setOpenExpired] = useState<boolean>(false);

  useEffect(() => {
    if (openExpired && data) {
      const droneNotification = data.notification;
      if (droneNotification.type === NotificationTypeEnum.DRONE) {
        updatePopupFromCockpit({
          mapId: MapIdEnum.LIVE,
          popup: {
            type: PopupTypeEnum.INFO,
            position: { x: 100, y: 300 },
            data: {
              type: InfoDataTypeEnum.INFO_EXPIRED_DRONE,
              notification: notificationLight,
            } as ExpiredDroneInfoData,
          },
        });
      }
      setOpenExpired(false);
    }
  }, [openExpired, data, updatePopupFromCockpit, notificationLight]);

  const handleOpenDroneInfoInLiveMap = () => {
    if (notificationLight.type !== NotificationTypeEnum.DRONE) {
      return;
    }
    if (isExpired) {
      !data &&
        !isLoading &&
        getDetail({ id: notificationLight.id, withArchivedAlerts: false, withDoneProcedures: false });
      setOpenExpired(true);
    } else {
      updatePopupFromCockpit({
        mapId: MapIdEnum.LIVE,
        popup: {
          type: PopupTypeEnum.INFO,
          position: { x: 100, y: 300 },
          data: {
            type: InfoDataTypeEnum.INFO_TARGET,
            targetId: notificationLight.identifier,
          } as TargetInfoData,
        },
      });
    }
  };

  return (
    <Center
      className="group"
      cursor="pointer"
      width="56px"
      height="100%"
      onClick={(e) => {
        centerMapView({
          mapId: MapIdEnum.LIVE,
          command: { type: 'CENTER', payload: { center: notificationLight.position, zoom: SHOW_ON_MAP_BUTTON_ZOOM } },
        });
        notificationLight.type === NotificationTypeEnum.DRONE && handleOpenDroneInfoInLiveMap();
        e.stopPropagation();
      }}
    >
      <Icon color="neutral.300" as={IcPin} boxSize="24px" _groupHover={{ color: 'skyBlue' }} />
    </Center>
  );
}
